import { FontSpec } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { useTheme } from "@mui/material";
import { CarPdfBox } from "@app/components/PdfBox";
import { CarPdfChartGridLegend } from "@app/components/PdfChartLegend";
import { CarPdfChart, CarPdfChartProps } from "@app/components/PdfChart";
import {
  CarAssetClassAnalysisItem,
  CarAssetClassAnalysisItemKey,
} from "../useAssetClassSummaryStat";
import { useAssetClassAnalysisChartData } from "../AssetClassAnalysisChart";

interface SummaryPdfChartProps
  extends Omit<CarPdfChartProps, "type" | "options" | "data" | "plugins"> {
  items: CarAssetClassAnalysisItem[];
  xStatId: CarAssetClassAnalysisItemKey;
  yStatId: CarAssetClassAnalysisItemKey;
  isWhiteContext?: boolean;
}

export const SummaryPdfChart = ({
  items,
  xStatId,
  yStatId,
  isWhiteContext,
  ...props
}: SummaryPdfChartProps) => {
  const theme = useTheme();

  const chartData = useAssetClassAnalysisChartData({
    items,
    xStatId,
    yStatId,
    forPdf: true,
  });
  const { datasets, periods, xLabel, yLabel, xFormatValue, yFormatValue } =
    chartData;

  const font: Partial<FontSpec> = {
    size: 9,
    weight: 400,
    lineHeight: 1,
    family: theme.typography.fontFamily,
  };

  const titleFont: Partial<FontSpec> = {
    ...font,
    size: 12,
    weight: 600,
  };

  return (
    <CarPdfBox>
      <CarPdfChart
        type="scatter"
        data={{ datasets }}
        options={{
          scales: {
            x: {
              title: {
                display: true,
                text: xLabel,
                color: theme.palette.softBlack,
                font: titleFont,
                padding: 10,
              },
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
                callback: (value) =>
                  typeof value === "number" ? xFormatValue(value) : value,
              },
              grid: ChartUtils.getGridOptions({ theme }),
            },
            y: {
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
                callback: (value) =>
                  typeof value === "number" ? yFormatValue(value) : value,
              },
              grid: ChartUtils.getGridOptions({ theme }),
              title: {
                display: true,
                text: yLabel,
                color: theme.palette.softBlack,
                font: titleFont,
              },
              offset: true,
            },
          },
          plugins: {
            roundedBackground: ChartUtils.getRoundedBackgroundOptions({
              theme,
              isWhiteContext,
            }),
            periods,
          },
        }}
        plugins={[ChartPlugins.roundedBackground, ChartPlugins.periods]}
        {...props}
      />
      <CarPdfChartGridLegend
        denseRows
        style={{ marginTop: 8 }}
        items={items.map((i) => ({
          label: i.name,
          color: String(i.color),
          isCircle: false,
        }))}
      />
    </CarPdfBox>
  );
};
